import { useCallback, useEffect, useMemo, useState } from 'react'
import type { SearchEngine } from '@/lib/search-engine'
import type { SearchableItem } from '@/lib/search-labels'

export type SortOption = 'relevance' | 'title' | 'category'
export type CategoryFilter = 'all' | 'blog' | 'project' | 'service' | 'page'

/**
 * `loading` until the engine arrives, `error` if it failed (never shown as
 * "no results"), `idle` while search has not been opened.
 */
export type SearchStatus = 'idle' | 'loading' | 'ready' | 'error'

let enginePromise: Promise<SearchEngine> | null = null

/**
 * Loads the search index and Fuse once per page session. The header renders
 * on every page but most visitors never search, so the ~105 KB index and
 * fuse.js ship as a separate chunk fetched on first use rather than in every
 * page's initial JavaScript. A failed load is forgotten so a retry fetches
 * again.
 */
export function loadSearchEngine(): Promise<SearchEngine> {
  enginePromise ??= import('@/lib/search-engine')
    .then(module => module.createSearchEngine())
    .catch((error: unknown) => {
      enginePromise = null
      throw error
    })
  return enginePromise
}

/**
 * Search state for the header overlay and the /search page. The engine loads
 * when `active` first becomes true; a query typed before it arrives is kept
 * and searched as soon as it does.
 */
export function useSearch({ active }: { active: boolean }) {
  const [query, setQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all')
  const [sortBy, setSortBy] = useState<SortOption>('relevance')
  const [engine, setEngine] = useState<SearchEngine | null>(null)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    // After a failure, wait for an explicit retry rather than reloading on
    // every reopen.
    if (!active || engine || failed) return
    let cancelled = false
    loadSearchEngine().then(
      loaded => {
        if (!cancelled) setEngine(loaded)
      },
      () => {
        if (!cancelled) setFailed(true)
      }
    )
    return () => {
      cancelled = true
    }
  }, [active, engine, failed, attempt])

  const retry = useCallback(() => {
    setFailed(false)
    setAttempt(count => count + 1)
  }, [])

  const status: SearchStatus = engine
    ? 'ready'
    : failed
      ? 'error'
      : active
        ? 'loading'
        : 'idle'

  // Perform search with category filtering and sorting
  const allResults = useMemo(() => {
    if (!engine) return []
    let results: SearchableItem[] = engine.search(query)

    // Apply category filter
    if (categoryFilter !== 'all') {
      results = results.filter(item => item.category === categoryFilter)
    }

    // Apply sorting
    if (sortBy === 'title') {
      results = [...results].sort((a, b) => a.title.localeCompare(b.title))
    } else if (sortBy === 'category') {
      results = [...results].sort((a, b) =>
        a.category.localeCompare(b.category)
      )
    }
    // 'relevance' is already sorted by Fuse.js score

    return results
  }, [engine, query, categoryFilter, sortBy])

  // Calculate category breakdown
  const categoryBreakdown = useMemo(() => {
    const breakdown = {
      blog: 0,
      project: 0,
      service: 0,
      page: 0,
    }
    allResults.forEach(item => {
      breakdown[item.category]++
    })
    return breakdown
  }, [allResults])

  const results = useMemo(() => {
    return allResults.slice(0, 8)
  }, [allResults])

  return {
    query,
    setQuery,
    results, // Limited to 8 for dropdown
    allResults, // All results for search page
    isSearching: query.trim().length > 0,
    status,
    retry,
    categoryFilter,
    setCategoryFilter,
    sortBy,
    setSortBy,
    categoryBreakdown,
  }
}
